import React, { useState, useEffect } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { siteInfo } from '../data/mock';
import { api } from '../api/client';
import { useSiteText } from '../hooks/useSiteText';

// Menu riorganizzato: niente "Home" (logo va alla home), Chi Siamo + Contatti raggruppati in "Info"
const baseNavItems = [
  { to: '/squadre', label: 'Squadre' },
  { to: '/calendario', label: 'Calendario' },
  { to: '/classifiche', label: 'Classifiche' },
  {
    label: 'Media',
    children: [
      { to: '/gallery', label: 'Galleria Foto' },
      { to: '/video', label: 'Video' },
    ],
  },
  { to: '/regolamento', label: 'Regolamento' },
  { to: '/partners', label: 'Partners' },
  { to: '/edizioni-passate', label: 'Edizioni Passate' },
  {
    label: 'Info',
    children: [
      { to: '/chi-siamo', label: 'Chi Siamo' },
      { to: '/contatti', label: 'Contatti' },
    ],
  },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [navItems, setNavItems] = useState(baseNavItems);
  // Cache localStorage per evitare il "flash" del logo vecchio al refresh
  const cachedLogo = (typeof window !== 'undefined') ? (window.localStorage.getItem('cachedLogoUrl') || '') : '';
  const [logoUrl, setLogoUrl] = useState(cachedLogo || siteInfo.logo);
  const [logoBroken, setLogoBroken] = useState(false);
  const logoLine1 = useSiteText('header.logo.line1', 'A.S.D.P.');
  const logoLine2 = useSiteText('header.logo.line2', 'Primavera');
  const logoLine3 = useSiteText('header.logo.line3', 'Cavese');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    let alive = true;
    api.getSettings().then((s) => {
      if (!alive) return;
      if (s?.logo_url) {
        setLogoUrl(s.logo_url);
        setLogoBroken(false);
        try { window.localStorage.setItem('cachedLogoUrl', s.logo_url); } catch { /* no-op */ }
      } else {
        // Nessun logo caricato → fallback default e pulisci cache stale
        try { window.localStorage.removeItem('cachedLogoUrl'); } catch { /* no-op */ }
        setLogoUrl(siteInfo.logo);
      }
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    api.listPages().then((pages) => {
      if (!alive) return;
      const menuPages = (pages || [])
        .filter((p) => p.published && p.show_in_menu)
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      if (menuPages.length === 0) return;
      const items = [...baseNavItems];
      // Inserisci sezione "Pagine" prima di "Info"
      const idx = items.findIndex((i) => i.label === 'Info');
      const node = { label: 'Pagine', children: menuPages.map((p) => ({ to: `/p/${p.slug}`, label: p.title })) };
      if (idx >= 0) items.splice(idx, 0, node); else items.push(node);
      setNavItems(items);
    }).catch(() => { /* fallback al menu base */ });
    return () => { alive = false; };
  }, []);

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-navy-dark/95 backdrop-blur-md shadow-lg' : 'bg-navy'}`}>
      <div className="max-w-[1500px] mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between py-3">
          {/* Logo più grande */}
          <Link to="/" className="flex items-center gap-3 group flex-shrink-0" data-testid="header-logo-link">
            <img
              src={logoBroken ? siteInfo.logo : logoUrl}
              alt={`${logoLine2} ${logoLine3}`}
              className="w-20 h-20 lg:w-24 lg:h-24 object-contain transition-transform duration-300 group-hover:scale-105"
              onError={() => {
                // Se il logo caricato è rotto, cadi sul default locale e pulisci cache stale
                if (!logoBroken) {
                  setLogoBroken(true);
                  try { window.localStorage.removeItem('cachedLogoUrl'); } catch { /* no-op */ }
                }
              }}
              data-testid="header-logo-img"
            />
            <div className="leading-tight hidden sm:block">
              <div className="text-[10px] lg:text-xs font-bold text-pink-brand uppercase tracking-widest">{logoLine1}</div>
              <div className="font-brand text-lg lg:text-xl font-bold text-white tracking-wide">
                {logoLine2 ? <>{logoLine2.charAt(0)}<span className="text-pink-brand">{logoLine2.slice(1)}</span></> : null}
              </div>
              <div className="font-brand text-lg lg:text-xl font-bold text-white tracking-wide -mt-1">
                {logoLine3 ? <>{logoLine3.charAt(0)}<span className="text-pink-brand">{logoLine3.slice(1)}</span></> : null}
              </div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-4 xl:gap-6">
            {navItems.map((item) => {
              if (item.children) {
                const isHovered = hovered === item.label;
                return (
                  <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => setHovered(item.label)}
                    onMouseLeave={() => setHovered(null)}
                  >
                    <button className="nav-link text-[12px] xl:text-sm font-semibold uppercase tracking-wider text-white/90 hover:text-white flex items-center gap-1 whitespace-nowrap">
                      {item.label} <ChevronDown size={14} className={isHovered ? 'rotate-180 transition-transform' : 'transition-transform'} />
                    </button>
                    {isHovered && (
                      <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 animate-fade-in">
                        <div className="bg-navy-dark border border-white/10 rounded-lg shadow-xl py-2 min-w-[180px]">
                          {item.children.map((c) => (
                            <NavLink
                              key={c.to}
                              to={c.to}
                              className={({ isActive }) =>
                                `block px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
                                  isActive ? 'text-pink-brand' : 'text-white/80 hover:text-white hover:bg-white/5'
                                }`
                              }
                            >
                              {c.label}
                            </NavLink>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `nav-link text-[12px] xl:text-sm font-semibold uppercase tracking-wider text-white/90 hover:text-white whitespace-nowrap ${
                      isActive ? 'active text-white' : ''
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          <button onClick={() => setOpen(!open)} className="lg:hidden text-white p-2 rounded-md hover:bg-white/10 transition-colors" aria-label="Menu">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden pb-4 animate-fade-in">
            <nav className="flex flex-col gap-1 py-2 border-t border-white/10">
              <NavLink
                to="/"
                end
                onClick={() => setOpen(false)}
                className={({ isActive }) => `px-4 py-3 rounded-lg font-semibold text-sm uppercase tracking-wider transition-colors ${isActive ? 'bg-pink-brand text-white' : 'text-white/90 hover:bg-white/10'}`}
              >
                Home
              </NavLink>
              {navItems.map((item) => {
                if (item.children) {
                  return (
                    <div key={item.label} className="pt-2">
                      <div className="px-4 py-2 text-white/50 text-[10px] uppercase tracking-widest font-bold">{item.label}</div>
                      {item.children.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          onClick={() => setOpen(false)}
                          className={({ isActive }) => `block px-4 py-3 rounded-lg font-semibold text-sm uppercase tracking-wider ${isActive ? 'bg-pink-brand text-white' : 'text-white/90 hover:bg-white/10'}`}
                        >
                          {c.label}
                        </NavLink>
                      ))}
                    </div>
                  );
                }
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) => `px-4 py-3 rounded-lg font-semibold text-sm uppercase tracking-wider transition-colors ${isActive ? 'bg-pink-brand text-white' : 'text-white/90 hover:bg-white/10'}`}
                  >
                    {item.label}
                  </NavLink>
                );
              })}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
